import { Loader2, Repeat } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useContactFollowUpSequences } from "@/hooks/useContactFollowUpSequences";
import { FOLLOW_UP_STATUS_LABELS, displayFollowUpStatus, isFollowUpStateLive } from "@/lib/followUpStatus";

interface LeadFollowUpSequencesProps {
  contactId: string;
}

/**
 * Card "Sequências" da aba lateral do chat: mostra em quais cadências o lead
 * está agora. Somente leitura — incluir e encerrar ficam no modal do cabeçalho.
 */
export function LeadFollowUpSequences({ contactId }: LeadFollowUpSequencesProps) {
  const { t } = useTranslation("chat");
  const { sequences, loading } = useContactFollowUpSequences(contactId);

  // Inscrições encerradas/concluídas viram histórico e poluiriam o painel.
  const live = sequences.filter((item) => isFollowUpStateLive(item.status));

  if (loading && sequences.length === 0) {
    return (
      <p className="text-xs text-muted-foreground dark:text-neutral-500 flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {t("common.loading")}
      </p>
    );
  }

  if (live.length === 0) {
    return (
      <p className="text-xs text-muted-foreground dark:text-neutral-500">
        {t("followUpSequence.emptyShort")}
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {live.map((state) => {
        const statusMeta = FOLLOW_UP_STATUS_LABELS[displayFollowUpStatus(state)];
        return (
          <li
            key={state.id}
            className="rounded-lg border border-border dark:border-neutral-700/50 bg-card-muted/40 dark:bg-neutral-800/40 p-2.5 flex flex-col gap-1.5"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <Repeat className="w-3.5 h-3.5 text-primary dark:text-blue-400 shrink-0" />
              <span className="text-sm font-medium text-foreground dark:text-neutral-100 truncate">
                {state.follow_up?.name ?? t("followUpSequence.unnamed")}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {statusMeta && (
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${statusMeta.className}`}>
                  {t(statusMeta.labelKey)}
                </span>
              )}
              {state.enrollment_source === "manual" && (
                <span className="text-[11px] text-muted-foreground dark:text-neutral-400">
                  {t("followUpSequence.manualBadge")}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
